import { useEffect, useMemo, useState } from 'react';

import { useBuildVars, useInX4AFrame, useSiteConfig } from 'mf_core/config';
import {
  useIsAuthenticated,
  useIsImpersonationMode,
  useDecodedToken,
} from 'mf_core/auth';
import { useLocalizationContext } from '@im/react-localization';
import { useFeatureFlagBase } from '../../hooks/use-feature-flag-base';
import { UserActivityTO, useUserActivity } from '../../hooks/useUserActivity';

const programKeyMap = {
  //dev: 'Jg4m6T',
  dev: 'QDWhcm',
  prod: 'LOpVXz',
};

export function useForstaFeedback() {
  const decodedToken = useDecodedToken();
  const isAuthenticated = useIsAuthenticated();
  const isImpersonationMode = useIsImpersonationMode();
  const { buildEnvironment } = useBuildVars();
  const { defaultLocale, siteCode } = useSiteConfig();
  const { isInX4AFrame } = useInX4AFrame();
  const { locale } = useLocalizationContext();

  const env = buildEnvironment === 'production' ? 'prod' : 'dev';

  const programKey = programKeyMap[env];
  const id = `cf-program-${programKey}`;
  const src = `https://digitalfeedback.us.confirmit.com/api/digitalfeedback/loader?programKey=${programKey}`;

  const { boValues: value, isReady } = useFeatureFlagBase();
  const isWhitelistedReseller =
    isReady && value['FORSTA_Feedback_Form_WhitelistedResellers'] === 'true';

  const forstaEnabled =
    isAuthenticated &&
    !isImpersonationMode &&
    !isInX4AFrame &&
    isWhitelistedReseller;

  const [isScriptInjected, setIsScriptInjected] = useState(false);

  const userActivityData = useUserActivity(forstaEnabled);

  useEffect(() => {
    // Inject Forsta Feedback Program Script in HTML Head
    if (forstaEnabled && !isScriptInjected) {
      const forstaScriptEl = document.createElement('script');
      forstaScriptEl.id = id;
      forstaScriptEl.type = 'text/javascript';
      forstaScriptEl.async = true;
      forstaScriptEl.src = src;
      document.head.appendChild(forstaScriptEl);
      setIsScriptInjected(true);
    }
  }, [forstaEnabled, id, isScriptInjected, src]);

  const [forstaUserCreated, setForstaUserCreated] = useState(false);
  const tokenData = useMemo(() => {
    return {
      country: siteCode,
      email: decodedToken?.imoProfile?.emailAddress || '',
      language: defaultLocale,
      resellerId: decodedToken?.resellerId || '',
    };
  }, [
    decodedToken?.imoProfile?.emailAddress,
    decodedToken?.resellerId,
    defaultLocale,
    siteCode,
  ]);

  useEffect(() => {
    if (forstaEnabled) {
      if (!window.forsta_user) {
        //Initialize Forsta globals
        window.forsta_user = {
          ...tokenData,
          ...userActivityData,
        };
        setForstaUserCreated(true);
      } else if (userActivityData) {
        //Update Forsta globals
        Object.assign(window.forsta_user, userActivityData);
      }
    }
  }, [forstaEnabled, tokenData, userActivityData]);

  useEffect(() => {
    //Update global language variable when locale changes
    if (forstaUserCreated && window.forsta_user.language !== locale)
      window.forsta_user.language = locale;
  }, [forstaUserCreated, locale]);
}

declare global {
  interface Window {
    forsta_user: {
      country: string;
      email: string;
      language: string;
      resellerId: string;
    } & UserActivityTO;
  }
}
